import { format as dateFnsFormat, isValid, Locale } from 'date-fns';
import { FormatFunction } from 'i18next';
import numeral from 'numeral';

import 'numeral/locales';

import { getDateFormat } from './formats';
import { userLocaleFetcher } from './localeFetcher';
import { defaultLocale, supportedLocales } from './supportedLocales';

const getLocales = (): {
  dateFnsLocale: Locale;
  numeralLocale: string;
} => {
  const {
    dateFnsLocale: defaultDateFnsLocale,
    numeralLocale: defaultNumeralLocale,
  } = defaultLocale;

  const { numeralLocale, dateFnsLocale } =
    supportedLocales[userLocaleFetcher.locale];

  return {
    dateFnsLocale: dateFnsLocale || defaultDateFnsLocale,
    numeralLocale: numeralLocale || defaultNumeralLocale,
  };
};

const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const format: FormatFunction = (value: any, format?: string): string => {
  if (format === undefined) return value as string;
  if (typeof value === 'string') {
    const f = format.toLowerCase();
    if (f === 'uppercase') return value.toUpperCase();
    if (f === 'lowercase') return value.toLowerCase();
    if (f === 'titlecase') return toTitleCase(value);
  }

  const { dateFnsLocale, numeralLocale } = getLocales();
  if (typeof value === 'number') {
    numeral.locale(numeralLocale);
    return numeral(value).format(format);
  }

  if (isValid(new Date(value))) {
    try {
      const dateFormat = getDateFormat(userLocaleFetcher.locale, format as any);
      return dateFnsFormat(new Date(value), dateFormat, {
        locale: dateFnsLocale,
      });
    } catch {
      console.error(
        `Error: date format invalid. Replace with locale date format. Format: ${format}`
      );
      return dateFnsFormat(value, format, { locale: dateFnsLocale });
    }
  } else console.error(`Error: Cannot format value: ${value}`);

  return value as string;
};

export default format;
